// sparkler-milestone — b06 Olov. Maximalist 30th milestone birthday.
//
// "Maximalist" here means visual magic + calming sound — NOT text-heavy
// information sections. Hero magic preserved: gold-foil "30", continuous
// firework bursts, twinkling sparkle field, oval gold-rimmed portrait.
// Bespoke "30 saboq" and "5 lahza" text blocks have been stripped, and
// the bespoke firework swoosh + sparkler-ignite cues replaced by the
// shared dignified-jazz ambient pad via `useAmbient`.

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { VariantProps } from "./types";
import { useSound } from "@/lib/sound";
import { useAmbient, useReducedMotion } from "./_ambient";
import { MusicToggle } from "@/components/MusicToggle";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
  GuestPhoto,
} from "@/components/sections";

// ─── Viewport guard ────────────────────────────────────────────────────

function useIsSmall(): boolean {
  const [small, setSmall] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setSmall(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return small;
}

// ─── Deterministic RNG for static fields ───────────────────────────────

function seeded(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  return x - Math.floor(x);
}

interface Twinkle { left: number; top: number; size: number; delay: number; dur: number; opacity: number; }
function buildTwinkles(n: number): Twinkle[] {
  return Array.from({ length: n }).map((_, i) => ({
    left: seeded(i + 1) * 100,
    top: seeded(i + 200) * 100,
    size: 1 + seeded(i + 400) * 3,
    delay: -seeded(i + 600) * 4,
    dur: 2.6 + seeded(i + 800) * 3.4,
    opacity: 0.45 + seeded(i + 1000) * 0.55,
  }));
}

// ─── Firework engine ────────────────────────────────────────────────────

interface Firework {
  id: number;
  left: number; // % within hero
  top: number;  // %
  color: string;
  size: number; // px diameter of burst
  rays: number;
}

const FIREWORK_PALETTE = ["#E2A630", "#C97B2C", "#A6391F", "#F4D27A", "#E2A630"];

function FireworkSVG({ fw }: { fw: Firework }) {
  const r = fw.size / 2;
  const rays = Array.from({ length: fw.rays });
  return (
    <svg
      width={fw.size}
      height={fw.size}
      viewBox={`-${r} -${r} ${fw.size} ${fw.size}`}
      style={{ display: "block", overflow: "visible" }}
    >
      <circle r={3} fill={fw.color} opacity={0.9} />
      {rays.map((_, i) => {
        const a = (i / rays.length) * Math.PI * 2;
        const x2 = Math.cos(a) * r;
        const y2 = Math.sin(a) * r;
        return (
          <g key={i}>
            <line
              x1={0}
              y1={0}
              x2={x2}
              y2={y2}
              stroke={fw.color}
              strokeWidth={1.2}
              strokeLinecap="round"
              opacity={0.85}
            />
            <circle cx={x2} cy={y2} r={2} fill={fw.color} />
            <circle cx={x2 * 0.6} cy={y2 * 0.6} r={1.1} fill="#F5EFE6" opacity={0.85} />
          </g>
        );
      })}
    </svg>
  );
}

// ─── Scroll-reveal hook ────────────────────────────────────────────────
function useScrollReveal() {
  useEffect(() => {
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") return;
    const els = Array.from(document.querySelectorAll<HTMLElement>(".b06-reveal"));
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            (e.target as HTMLElement).classList.add("b06-reveal-on");
            obs.unobserve(e.target);
          }
        }
      },
      { threshold: 0.12 },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

// ─── Variant ───────────────────────────────────────────────────────────

export function SparklerMilestoneVariant({ t, ctx }: VariantProps) {
  const { play } = useSound();
  const reduce = useReducedMotion();
  const small = useIsSmall();
  const photos = t.photos || [];
  const cleanName = useMemo(
    () => t.names.replace(/\s*[·•]?\s*\d+\s*(yosh|yil)?\s*$/i, "").trim() || t.names,
    [t.names],
  );
  // Pull a numeric age out of "Sherzod 30 yosh" / "Sherzod · 30" / fallback 30.
  const ageNumber = useMemo(() => {
    const m = t.names.match(/(\d{1,3})\s*(yosh|yil)?\s*$/i);
    return m ? m[1] : "30";
  }, [t.names]);

  // Force the dark-mode skin regardless of host palette. The whole identity
  // of b06 is the night sky.
  const sky = "#0E0B14";
  const cream = "#F5EFE6";
  const gold = "#E2A630";

  // ── Twinkle field (stable across renders) ──
  const twinklesRef = useRef<Twinkle[]>(buildTwinkles(120));
  const twinkles = twinklesRef.current;

  // ── Firework engine state ──
  const [fireworks, setFireworks] = useState<Firework[]>([]);
  const fwIdRef = useRef(0);

  // Spawn one firework now (also called periodically by the engine).
  const spawnFirework = useMemo(
    () => () => {
      fwIdRef.current += 1;
      const id = fwIdRef.current;
      const color = FIREWORK_PALETTE[id % FIREWORK_PALETTE.length];
      const fw: Firework = {
        id,
        left: 8 + seeded(id) * 84,
        top: 8 + seeded(id + 99) * 60,
        color,
        size: 120 + seeded(id + 7) * 140,
        rays: 14 + Math.floor(seeded(id + 13) * 6),
      };
      setFireworks((prev) => [...prev, fw]);
      // Auto-remove after burst finishes.
      window.setTimeout(() => {
        setFireworks((prev) => prev.filter((x) => x.id !== id));
      }, 1500);
    },
    [],
  );

  // Continuous engine — runs only when motion + viewport allow.
  useEffect(() => {
    if (reduce || small) return;
    // Seed two bursts so the hero is alive immediately.
    const t0 = window.setTimeout(spawnFirework, 400);
    const t1 = window.setTimeout(spawnFirework, 1400);
    let timer: number | null = null;
    const schedule = () => {
      const wait = 3000 + Math.random() * 1500;
      timer = window.setTimeout(() => {
        spawnFirework();
        schedule();
      }, wait);
    };
    schedule();
    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      if (timer != null) window.clearTimeout(timer);
    };
  }, [reduce, small, spawnFirework]);

  // ── Ambient soundscape (dignified jazz pad) ──
  const [music, setMusic] = useState(false);
  useAmbient(music, {
    freqs: [130.81, 174.61, 220.0, 261.63],
    type: "sine",
    master: 0.03,
    lfo: 0.08,
  });

  useScrollReveal();

  // Single hero swoosh on mount.
  useEffect(() => {
    if (reduce) return;
    const id = window.setTimeout(() => play("swoosh"), 600);
    return () => window.clearTimeout(id);
  }, [reduce, play]);

  // ── RSVP wrapper — chime on yes only ──
  const wrappedSetRsvp = (v: string | null) => {
    if (v === "yes") play("chime");
    ctx.rsvp.setRsvp(v);
  };

  // Themed `t` for shared sections (dark sky palette).
  const tDark = { ...t, bg: sky, ink: cream, accent: gold };

  return (
    <div
      className="b06-olov"
      style={{
        background: sky,
        color: cream,
      }}
    >
      <style>{`
        @keyframes b06-twinkle {
          0%, 100% { opacity: var(--b06-tw-op, 0.6); transform: scale(1); }
          50%      { opacity: 0.05; transform: scale(0.4); }
        }
        @keyframes b06-burst {
          0%   { transform: translate(-50%, -50%) scale(0.05); opacity: 0; }
          25%  { opacity: 1; }
          70%  { transform: translate(-50%, -50%) scale(1); opacity: 0.95; }
          100% { transform: translate(-50%, -50%) scale(1.18); opacity: 0; }
        }
        @keyframes b06-foil-sweep {
          0%   { background-position: -200% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes b06-rsvp-glow {
          0%, 100% { box-shadow: 0 0 0 0 ${gold}55, 0 0 18px 4px ${gold}33; }
          50%      { box-shadow: 0 0 0 8px ${gold}00, 0 0 32px 12px ${gold}77; }
        }

        .b06-olov .b06-30 {
          font-family: var(--font-display);
          font-style: italic;
          font-weight: 400;
          line-height: 0.9;
          letter-spacing: -0.05em;
          background: linear-gradient(100deg,
            #8E5A1A 0%,
            #C97B2C 18%,
            #F4D27A 38%,
            #FFFFFF 50%,
            #F4D27A 62%,
            #C97B2C 82%,
            #8E5A1A 100%);
          background-size: 200% 100%;
          background-position: 50% 50%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: #E2A630;
          text-shadow: 0 0 60px rgba(226, 166, 48, 0.45);
          animation: b06-foil-sweep 2.6s ease-out 0.2s 1;
        }

        .b06-reveal { opacity: 0; transform: translateY(24px); transition: opacity 700ms ease, transform 700ms ease; }
        .b06-reveal-on { opacity: 1; transform: translateY(0); }

        .b06-olov [class*="schedule"] li,
        .b06-olov [class*="schedule"] tr,
        .b06-olov [class*="schedule"] [role="row"] {
          transition: transform 220ms ease, background 220ms ease;
        }
        .b06-olov [class*="schedule"] li:hover,
        .b06-olov [class*="schedule"] tr:hover,
        .b06-olov [class*="schedule"] [role="row"]:hover {
          transform: scale(1.015);
          background: ${gold}10;
        }

        .b06-olov button[data-rsvp="yes"],
        .b06-olov button[aria-pressed="true"][data-value="yes"] {
          animation: b06-rsvp-glow 2.4s ease-in-out infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .b06-olov .b06-twinkle { animation: none !important; opacity: var(--b06-tw-op, 0.6) !important; }
          .b06-olov .b06-30 { animation: none !important; background-position: 50% 50% !important; }
          .b06-olov .b06-burst-anim { animation: none !important; opacity: 0.85 !important; }
          .b06-reveal { opacity: 1 !important; transform: none !important; transition: none !important; }
          .b06-olov button[data-rsvp="yes"],
          .b06-olov button[aria-pressed="true"][data-value="yes"] { animation: none !important; }
        }

        @media (max-width: 768px) {
          .b06-olov .b06-hero { padding: 64px 18px 48px !important; gap: 24px !important; }
          .b06-olov .b06-30 { font-size: clamp(140px, 42vw, 220px) !important; }
          .b06-olov .b06-names {
            font-size: clamp(24px, 7vw, 48px) !important;
            flex-direction: column !important;
            gap: 4px !important;
            word-break: break-word !important;
            white-space: normal !important;
            text-align: center !important;
          }
          .b06-olov .b06-portrait {
            width: min(160px, 48vw) !important;
            height: min(200px, 60vw) !important;
          }
          /* Date/venue flex row: allow wrap and shrink */
          .b06-olov .b06-datevenue {
            font-size: 10px !important;
            letter-spacing: 0.22em !important;
            word-break: break-word !important;
            white-space: normal !important;
          }
          /* Hide the decorative line spans that add width to the date row */
          .b06-olov .b06-datevenue-line { display: none !important; }
          .b06-olov .b06-section { padding: 64px 18px !important; }
          .b06-olov .b06-grid { grid-template-columns: 1fr !important; gap: 24px !important; max-width: 560px !important; margin: 0 auto !important; }
          .b06-olov button,
          .b06-olov a[role="button"] { min-height: 44px; }
        }
        .b06-olov button:focus-visible,
        .b06-olov a:focus-visible {
          outline: 2px solid ${gold};
          outline-offset: 3px;
        }
      `}</style>

      {/* ═══════════════════════════════════════════════════════════════
          HERO
          ═══════════════════════════════════════════════════════════════ */}
      <section
        className="b06-hero"
        style={{
          position: "relative",
          minHeight: "100vh",
          padding: "100px 48px 120px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 36,
          overflow: "hidden",
          background: `radial-gradient(ellipse at 50% 30%, #1B1326 0%, ${sky} 60%)`,
        }}
      >
        {/* Twinkle layer */}
        <div aria-hidden style={{ position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none" }}>
          {twinkles.map((tw, i) => (
            <span
              key={i}
              className="b06-twinkle"
              style={{
                position: "absolute",
                left: `${tw.left}%`,
                top: `${tw.top}%`,
                width: tw.size,
                height: tw.size,
                borderRadius: "50%",
                background: i % 6 === 0 ? gold : cream,
                boxShadow: i % 6 === 0 ? `0 0 6px ${gold}` : `0 0 3px ${cream}aa`,
                opacity: tw.opacity,
                animation: `b06-twinkle ${tw.dur}s ease-in-out ${tw.delay}s infinite`,
                ["--b06-tw-op" as string]: String(tw.opacity),
              } as CSSProperties}
            />
          ))}
        </div>

        {/* Live firework bursts layer */}
        <div aria-hidden style={{ position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none" }}>
          {fireworks.map((fw) => (
            <div
              key={fw.id}
              className="b06-burst-anim"
              style={{
                position: "absolute",
                left: `${fw.left}%`,
                top: `${fw.top}%`,
                transform: "translate(-50%, -50%) scale(0.05)",
                animation: `b06-burst 1.4s ease-out forwards`,
              }}
            >
              <FireworkSVG fw={fw} />
            </div>
          ))}
        </div>

        {/* Eyebrow */}
        <div
          style={{
            position: "relative",
            zIndex: 2,
            fontFamily: "var(--font-ui)",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.5em",
            color: gold,
            textAlign: "center",
            paddingLeft: "0.5em",
          }}
        >
          TANTANALI YIL
        </div>

        {/* Oval portrait */}
        <div
          className="b06-portrait"
          style={{
            position: "relative",
            zIndex: 2,
            width: 220,
            height: 280,
            borderRadius: "50% / 44%",
            padding: 4,
            background: `linear-gradient(155deg, ${gold}, #C97B2C 60%, #8E5A1A)`,
            boxShadow: `0 18px 60px ${gold}66, 0 0 0 1px ${gold}33`,
          }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              borderRadius: "50% / 44%",
              overflow: "hidden",
              background: sky,
              position: "relative",
            }}
          >
            <GuestPhoto
              src={photos[0]}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
              fallbackBg={sky}
              fallbackTone={gold}
            />
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                background: `linear-gradient(180deg, transparent 50%, ${sky}cc 100%)`,
                pointerEvents: "none",
              }}
            />
          </div>
        </div>

        {/* GIANT centerpiece — driven by ageNumber parsed from t.names */}
        <h1
          className="b06-30"
          style={{
            position: "relative",
            zIndex: 2,
            margin: 0,
            fontSize: "clamp(220px, 32vw, 440px)",
          }}
        >
          {ageNumber}
        </h1>

        {/* Names */}
        <div
          className="b06-names"
          style={{
            position: "relative",
            zIndex: 2,
            fontFamily: "var(--font-display)",
            fontStyle: "italic",
            fontSize: "clamp(48px, 7vw, 88px)",
            fontWeight: 400,
            color: gold,
            lineHeight: 1,
            textAlign: "center",
            letterSpacing: "-0.015em",
          }}
        >
          {cleanName} · {ageNumber} yosh
        </div>

        {/* Date · venue */}
        <div
          className="b06-datevenue"
          style={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            gap: 16,
            fontFamily: "var(--font-ui)",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.36em",
            color: cream,
            textAlign: "center",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <span className="b06-datevenue-line" style={{ width: 32, height: 1, background: gold, opacity: 0.7 }} />
          <span>{t.date}</span>
          <span style={{ color: gold }}>·</span>
          <span>{t.venue.toUpperCase()}</span>
          <span className="b06-datevenue-line" style={{ width: 32, height: 1, background: gold, opacity: 0.7 }} />
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          COUNTDOWN
          ═══════════════════════════════════════════════════════════════ */}
      <div className="b06-reveal">
        <CountdownSection t={tDark} {...ctx.countdown} isDark={true} />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          STORY — trimmed to 2-3 sentences
          ═══════════════════════════════════════════════════════════════ */}
      <div className="b06-reveal">
        <StorySection
          t={tDark}
          eyebrow={`${ageNumber} yilning hisobi`}
          title="Bir umrlik fasl"
          body={`${ageNumber} yosh — bu raqam emas, bu hisob-kitob. Orqaga qarab minglab kunlar, oldinga qarab haligacha aytilmagan ko'plab niyatlar. Sizni shu kechada biz bilan bo'lishga taklif qilamiz.`}
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          GALLERY
          ═══════════════════════════════════════════════════════════════ */}
      <div className="b06-reveal">
        <GallerySection t={tDark} />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          SCHEDULE
          ═══════════════════════════════════════════════════════════════ */}
      <div className="b06-reveal">
        <ScheduleSection
          t={tDark}
          items={[
            ["18:00", "Mehmonlarning kutib olinishi"],
            ["18:30", "Koktel va mualliflik suvlari"],
            ["19:00", "Ochilish tosti"],
            ["20:00", "Tantanali dasturxon"],
            ["21:00", "30 sparkler yondiriladi"],
            ["22:00", "Osmondagi feyerverk"],
            ["23:00", "Tortning yurishi"],
            ["00:00", "Yarim tundagi maxsus sirpriz"],
          ]}
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          MAP
          ═══════════════════════════════════════════════════════════════ */}
      <div className="b06-reveal">
        <MapSection
          t={tDark}
          isDark={true}
          addressLine="International Hotel Tashkent, Amir Temur ko'chasi 107A, Toshkent"
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          RSVP
          ═══════════════════════════════════════════════════════════════ */}
      <div className="b06-reveal">
        <RsvpSection
          t={tDark}
          isDark={true}
          rsvp={ctx.rsvp.rsvp}
          setRsvp={wrappedSetRsvp}
          guests={ctx.rsvp.guests}
          setGuests={ctx.rsvp.setGuests}
          guestName={ctx.rsvp.guestName}
          setGuestName={ctx.rsvp.setGuestName}
          label={`${ageNumber} yilligimni biz bilan nishonlang`}
          slug={ctx.slug}
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          SHARE
          ═══════════════════════════════════════════════════════════════ */}
      <div className="b06-reveal">
        <ShareSection t={tDark} />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          FOOTER
          ═══════════════════════════════════════════════════════════════ */}
      <GuestFooter t={tDark} />

      {/* Floating ambient music toggle */}
      <MusicToggle
        on={music}
        onToggle={() => setMusic((v) => !v)}
        accent={t.accent}
        ink={t.ink}
        style={{ bottom: 18, right: 18 }}
        className="b06-music-toggle"
      />
    </div>
  );
}
